import filter from "lodash/filter";
import includes from "lodash/includes";
import keys from "lodash/keys";
import map from "lodash/map";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { getCvExportsPath } from "PFApp/components/cv_exports_button";
import { FiltersV2Button, FiltersV2Preview, useFiltersV2Select } from "PFApp/components/filters_v2";
import { generateApiFilters } from "PFApp/components/filters_v2/helpers/generate_api_filters";
import { SearchEntity } from "PFApp/components/search_autocomplete/search_autocomplete.types";
import { SearchInput } from "PFApp/components/search_input";
import { TablePropertiesButton } from "PFApp/components/table_properties/table_properties_button";
import { DEFAULT_PAGE, PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { Button } from "PFComponents/button";
import { Inline } from "PFComponents/containers";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Typography } from "PFComponents/typography";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { fetchActivityWatchers } from "PFCore/services/activities";
import { FeatureFlag, PermissionRule } from "PFTypes";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { RoleWatcher } from "../../../../../types/watchers";
import RefreshAction from "../../../../parts/refresh_action";
import useFormatWatchersToCsv from "./hooks/use_format_watchers_to_csv";
import css from "./interested.module.scss";
import { useInterestedContext } from "./interested_context";

const getSelectedProfiles = (watchers: RoleWatcher[], selectedIds: Record<string, true>) =>
  filter(watchers, ({ id }) => includes(keys(selectedIds), String(id)));

const getWatchersProfilesIds = (watchers: RoleWatcher[], selectedIds: Record<string, true>) =>
  map(getSelectedProfiles(watchers, selectedIds), "profile.id");

const InterestedPageHeader = () => {
  const {
    setTablePropertiesVisibility,
    selectedIds,
    watchers,
    columns,
    watchersMetadata,
    searchTerm,
    setSearchTerm
  } = useInterestedContext();
  const { t } = useTranslation("activities", { keyPrefix: "show.interested" });
  const { activityId } = useActivityShowContext();
  const formatWatchers = useFormatWatchersToCsv(columns);
  const filtersV2Meta = useFiltersV2Select("mainFilters", "childrenFilters", "areChildrenOn");
  const { invalidateWatchers } = useActivityInvalidate();
  const navigate = useNavigate();
  const isFeatureEnabled = useIsFeatureEnabled();
  const isProfileExportEnabled = isFeatureEnabled(FeatureFlag.ProfileExporter);
  const canExportDocs = useIsCurrentUserPermittedTo(PermissionRule.ExportDocuments);
  const profilesForExport =
    keys(selectedIds).length > 0 ? getSelectedProfiles(watchers, selectedIds) : watchers;

  const handleDownloadTableCsv = async () => {
    const res = await fetchActivityWatchers(
      activityId,
      DEFAULT_PAGE,
      PER_PAGE_UNPAGINATED,
      generateApiFilters(filtersV2Meta)
    );
    const formatted = formatWatchers(res.entries);
    if (!formatted.length) {
      return;
    }
    const { default: saveAs } = await import("file-saver");
    const headers = Object.keys(formatted[0]).join(",");
    const rows = formatted.map((row) => Object.values(row).join(",")).join("\n");
    const blob = new Blob([headers, "\n", rows], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "Table data.csv");
  };

  const exportOptions: DropdownOption[] = [
    {
      id: "export-cvs",
      displayElement: t("export.exportCVs"),
      item: {
        func: () =>
          navigate(
            getCvExportsPath(
              profilesForExport.map(({ profile }) => profile.id),
              activityId
            )
          )
      }
    },
    {
      id: "export-csv",
      displayElement: t("export.exportAsCsv"),
      item: { func: handleDownloadTableCsv }
    }
  ];

  const compareSelectedProfiles = () => {
    navigate(
      {
        pathname: `/activities/${activityId}/compare/details/${getWatchersProfilesIds(
          watchers,
          selectedIds
        ).join(",")}/interested`
      },
      { state: { prevPath: location.pathname } }
    );
  };

  const showExportButton = isProfileExportEnabled && canExportDocs && profilesForExport.length > 0;

  return (
    <Inline spread alignItems="center">
      <Inline alignItems="center">
        <SearchInput
          term={searchTerm}
          placeholder={t("table.termSearch.placeholder")}
          pageKey={RecentSearchesPageKey.ActivityInterested}
          autocompleteConfig={[
            {
              entity: SearchEntity.Profile,
              redirect: true
            }
          ]}
          onTermChange={setSearchTerm}
          qaId="ActivityInterestedTabSearchTermInput"
        />

        <Typography className={css.counter} variant="labelRegular" clipOverflow>
          {t("counter", { total: watchersMetadata?.total })}
        </Typography>
      </Inline>

      <FiltersV2Preview omit={[]} />
      <Inline>
        <RefreshAction
          onUpdate={() => invalidateWatchers(activityId)}
          activityId={activityId}
          showLabel={false}
          qaId="ActivityInterestedTabRefreshMatchesButton"
        />
        <Button
          icon="compare"
          kind="secondary"
          onClick={compareSelectedProfiles}
          disabled={keys(selectedIds).length < 2}
          qaId="ActivityInterestedTabCompareButton"
        />
        {showExportButton && (
          <DropdownButton
            icon="export"
            buttonKind="secondary"
            options={exportOptions}
            handleChange={(item) => item.func()}
            qaId="ActivityInterestedTabExportButton"
          />
        )}
        <TablePropertiesButton onClick={() => setTablePropertiesVisibility(true)} />
        <FiltersV2Button />
      </Inline>
    </Inline>
  );
};

export default InterestedPageHeader;
